import React, { useState, useEffect, useCallback } from "react";
import Table from "@mui/joy/Table";
import { Box, Button, Stack, Typography } from "@mui/joy";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import { ModalDialog } from "@mui/joy";
import Divider from "@mui/joy/Divider";
import { FormControl } from "@mui/material";
import TourIcon from "@mui/icons-material/Tour";
import Input from "@mui/joy/Input";
import { UserAuth } from "../../context/AuthContext";
import { db } from "../../FirebaseConfig";
import {
  addDoc,
  collection,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  getDoc,
  updateDoc,
  increment,
  arrayUnion,
} from "firebase/firestore";
import Confetti from "react-confetti";
import { toast } from "react-toastify";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LinkIcon from "@mui/icons-material/Link";
import FlagIcon from "@mui/icons-material/Flag";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

interface Challenge {
  id: string;
  title: string;
  category: string;
  difficulty: string;
  points: number;
  description: string;
  flag: string;
  hints: { hint: string; points: number }[];
  fileUrl?: string;
}

interface CTFChallengeTableProps {
  challenges: Challenge[];
  eventname: string;
}

const CTFChallengeTable: React.FC<CTFChallengeTableProps> = ({
  challenges,
  eventname,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(
    null
  );
  const [flag, setFlag] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const { userData } = UserAuth();
  const [submittedChallenges, setSubmittedChallenges] = useState<string[]>([]);
  const [totalScore, setTotalScore] = useState<number>(0);
  const [revealedHints, setRevealedHints] = useState<number[]>([]);
  const [unlockedHints, setUnlockedHints] = useState<{
    [key: string]: number[];
  }>({});
  const [submissionCounts, setSubmissionCounts] = useState<{
    [key: string]: number;
  }>({});
  const [solveCounts, setSolveCounts] = useState<{ [key: string]: number }>({});

  const typeConfig: { [key: string]: { color: string } } = {
    Easy: { color: "success" },
    Moderate: { color: "warning" },
    Hard: { color: "danger" },
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (userData && userData?.id) {
        const registrationRef = doc(
          db,
          `Events/${eventname.toLowerCase()}/Registrations`,
          userData?.id
        );
        const registrationDoc = await getDoc(registrationRef);
        if (registrationDoc.exists()) {
          const data = registrationDoc.data();
          setTotalScore(data.points || 0);
          setSubmittedChallenges(data.completedChallenges || []);
          setUnlockedHints(data.unlockedHints || {});
        }

        // Fetch submission counts
        const submissionsRef = doc(
          db,
          `Events/${eventname.toLowerCase()}/Submissions`,
          userData?.id
        );
        const submissionsDoc = await getDoc(submissionsRef);
        if (submissionsDoc.exists()) {
          const data = submissionsDoc.data();
          const counts: { [key: string]: number } = {};
          Object.keys(data).forEach((challengeId) => {
            counts[challengeId] = data[challengeId].length;
          });
          setSubmissionCounts(counts);
        }
      }
    };
    fetchUserData();
  }, [userData, eventname]);

  useEffect(() => {
    const fetchSolveCounts = async () => {
      try {
        const solveCountsRef = doc(
          db,
          `Events/${eventname.toLowerCase()}/Statistics`,
          "solveCounts"
        );
        const solveCountsDoc = await getDoc(solveCountsRef);

        if (solveCountsDoc.exists()) {
          setSolveCounts(solveCountsDoc.data() || {});
        } else {
          // Initialize the document if it doesn't exist
          await setDoc(solveCountsRef, {});
          setSolveCounts({});
        }
      } catch (error) {
        console.error("Error fetching solve counts:", error);
        toast.error("Failed to load challenge statistics");
      }
    };

    fetchSolveCounts();
  }, [eventname]);

  const handleOpenModal = (challenge: Challenge) => {
    setSelectedChallenge(challenge);
    setOpen(true);
    setIsCorrect(submittedChallenges.includes(challenge.id));
    setFlag("");
    setRevealedHints([]);
  };

  const handleSubmitFlag = async () => {
    if (!selectedChallenge || !userData) return;

    const submissionsRef = doc(
      db,
      `Events/${eventname.toLowerCase()}/Submissions`,
      userData?.id
    );
    const registrationRef = doc(
      db,
      `Events/${eventname.toLowerCase()}/Registrations/`,
      userData?.id
    );
    const solveCountsRef = doc(
      db,
      `Events/${eventname.toLowerCase()}/Statistics`,
      "solveCounts"
    );

    const categoryCountsRef = doc(
      db,
      `Events/${eventname.toLowerCase()}/Statistics`,
      "categoryCounts"
    );

    const submissionStatsRef = doc(
      db,
      `Events/${eventname.toLowerCase()}/Statistics`,
      "submissionStats"
    );
    const Success = "Success";
    const Failure = "Failure";
    try {
      // Check submission count
      const currentSubmissionCount =
        submissionCounts[selectedChallenge.id] || 0;

      if (currentSubmissionCount >= 10) {
        toast.error("You have no more attempts left for this challenge.");
        return;
      }

      // Update Submissions document
      await setDoc(
        submissionsRef,
        {
          [selectedChallenge.id]: arrayUnion({
            flag: flag,
            timestamp: new Date(),
            correct: flag === selectedChallenge.flag,
          }),
        },
        { merge: true }
      );

      // Update submission count
      setSubmissionCounts((prev) => ({
        ...prev,
        [selectedChallenge.id]: (prev[selectedChallenge.id] || 0) + 1,
      }));

      if (flag === selectedChallenge.flag) {
        // Get current registration data
        const registrationDoc = await getDoc(registrationRef);
        if (registrationDoc.exists()) {
          const currentData = registrationDoc.data();
          const currentPoints = currentData.points || 0;
          const completedChallenges = currentData.completedChallenges || [];
          const solvedChallenges = currentData.solvedChallenges || [];

          // Check if user has already solved this challenge
          if (!completedChallenges.includes(selectedChallenge.id)) {
            // Update solve counts atomically
            await setDoc(
              solveCountsRef,
              {
                [selectedChallenge.id]: increment(1),
              },
              { merge: true }
            );

            await setDoc(
              categoryCountsRef,
              {
                [selectedChallenge.category]: increment(1),
              },
              { merge: true }
            );

            await setDoc(
              submissionStatsRef,
              {
                Success: increment(1),
              },
              { merge: true }
            );
            // Update local solve counts state
            setSolveCounts((prev) => ({
              ...prev,
              [selectedChallenge.id]: (prev[selectedChallenge.id] || 0) + 1,
            }));

            // Update user's points and completed challenges
            const updatedPoints = parseInt(currentPoints) + selectedChallenge.points;
            const updatedCompletedChallenges = [
              ...completedChallenges,
              selectedChallenge.id,
            ];

            const updatedSolvedChallenges = [
              ...solvedChallenges,
              {
                title: selectedChallenge.title,
                challengeId: selectedChallenge.id,
                solvedAt: new Date(),
                type: selectedChallenge.category,
                points: selectedChallenge.points,
              },
            ];

            await updateDoc(registrationRef, {
              points: updatedPoints,
              completedChallenges: updatedCompletedChallenges,
              solvedChallenges: updatedSolvedChallenges,
            });

            // Update local states
            setTotalScore(updatedPoints);
            setSubmittedChallenges(updatedCompletedChallenges);

            toast.success(
              `Correct flag! ${selectedChallenge.points} points added to your score.`
            );
          } else {
            toast.info("You've already solved this challenge!");
          }

          setIsCorrect(true);
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 5000);
        }
      } else {
        // Handle incorrect flag submission
        if (currentSubmissionCount === 9) {
          toast.error("Incorrect flag. You have no more attempts left.");
        } else {
          await setDoc(
            submissionStatsRef,
            {
              Failure: increment(1),
            },
            { merge: true }
          );
        }
        toast.error("Incorrect flag. Try again!");
      }
    } catch (error) {
      console.error("Error submitting flag:", error);
      toast.error(
        "An error occurred while submitting the flag. Please try again."
      );
    }
  };

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter') {
        handleSubmitFlag();
      }
    },
    [ handleSubmitFlag ]
  );

  const handleRevealHint = async (hintIndex: number) => {
    if (!selectedChallenge || !userData) return;

    const registrationRef = doc(
      db,
      `Events/${eventname.toLowerCase()}/Registrations`,
      userData?.id
    );

    try {
      const registrationDoc = await getDoc(registrationRef);

      if (registrationDoc.exists()) {
        const currentData = registrationDoc.data();
        const currentUnlockedHints = currentData.unlockedHints || {};
        const challengeUnlockedHints =
          currentUnlockedHints[selectedChallenge.id] || [];

        if (!challengeUnlockedHints.includes(hintIndex)) {
          const hintPoints = selectedChallenge.hints[hintIndex].points;
          const currentPoints = currentData.points || 0;

          if (currentPoints >= hintPoints) {
            // Add confirmation dialog
            const confirmUnlock = confirm(
              `Are you sure you want to unlock Hint ${
                hintIndex + 1
              }? This will cost you ${hintPoints} points.`
            );

            if (!confirmUnlock) {
              return; // User cancelled the hint unlock
            }

            const newUnlockedHints = {
              ...currentUnlockedHints,
              [selectedChallenge.id]: [...challengeUnlockedHints, hintIndex],
            };

            const updatedPoints = currentPoints - hintPoints;

            await updateDoc(registrationRef, {
              points: updatedPoints,
              unlockedHints: newUnlockedHints,
            });

            setTotalScore(updatedPoints);
            setUnlockedHints(newUnlockedHints);
            setRevealedHints((prev) => [...prev, hintIndex]);

            toast.info(`Hint unlocked! ${hintPoints} points deducted.`);
          } else {
            toast.error("Not enough points to unlock this hint.");
          }
        } else {
          // If hint is already unlocked, just toggle its visibility
          setRevealedHints((prev) =>
            prev.includes(hintIndex)
              ? prev.filter((i) => i !== hintIndex)
              : [...prev, hintIndex]
          );
        }
      }
    } catch (error) {
      console.error("Error revealing hint:", error);
      toast.error(
        "An error occurred while revealing the hint. Please try again."
      );
    }
  };
  return (
    <>
      <Table aria-label="basic table" sx={{ textAlign: "center" }}>
        <thead>
          <tr>
            <th style={{ width: "30%", textAlign: "center" }}>Name</th>
            <th style={{ width: "20%", textAlign: "center" }}>Category</th>
            <th style={{ width: "20%", textAlign: "center" }}>Difficulty</th>
            <th style={{ width: "20%", textAlign: "center" }}>Points</th>
            <th style={{ width: "20%", textAlign: "center" }}></th>
          </tr>
        </thead>
        <tbody>
          {challenges.map((challenge) => (
            <tr key={challenge.id}>
              <td>{challenge.title}</td>
              <td>{challenge.category}</td>
              <td>
                <Typography
                  color={
                    (typeConfig[challenge.difficulty]?.color as any) ||
                    "neutral"
                  }
                >
                  {challenge.difficulty}
                </Typography>
              </td>
              <td>{challenge.points}</td>
              <td>
                <Button
                  color="primary"
                  size="md"
                  variant="outlined"
                  disabled={submittedChallenges.includes(challenge.id)}
                  onClick={() => handleOpenModal(challenge)}
                >
                  {submittedChallenges.includes(challenge.id)
                    ? "Submitted"
                    : "Flag"}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {selectedChallenge && (
        <Modal
          open={open}
          onClose={() => setOpen(false)}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            transform: "translateX(120px)",
          }}
        >
          <ModalDialog
            layout="center"
            size="lg"
            sx={{
              width: "800px",
              borderRadius: "12px",
              boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
              padding: "24px",
              overflowY: "auto",
            }}
          >
            <Typography
              level="h2"
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "24px",
                marginBottom: "16px",
              }}
            >
              Challenge Details
            </Typography>
            <ModalClose
              sx={{
                top: "16px",
                right: "16px",
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
              }}
            >
              {/* Challenge Info */}
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{
                  paddingBottom: "12px",
                  borderBottom: "1px dashed #444",
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography level="title-md" sx={{ fontWeight: "bold" }}>
                    Category
                  </Typography>
                  <Typography level="body-md">
                    {selectedChallenge.category}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <Typography level="title-md" sx={{ fontWeight: "bold" }}>
                    Points
                  </Typography>
                  <Typography level="body-md">
                    {selectedChallenge.points}
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ textAlign: "center", marginBottom: "12px" }}>
                <Typography
                  level="h3"
                  sx={{
                    fontWeight: "bold",
                    fontSize: "22px",
                    marginBottom: "8px",
                  }}
                >
                  {selectedChallenge.title}
                </Typography>
                <Typography
                  level="body-md"
                  sx={{ fontSize: "16px", lineHeight: "1.6" }}
                >
                  {selectedChallenge.description}
                </Typography>
              </Box>
              {selectedChallenge.fileUrl ? (
                      ["Web Exploitation"].includes(selectedChallenge.category) ? (
                        <Button
                          variant="outlined"
                          startDecorator={<LinkIcon />} // Assuming a link icon for "View Site"
                          onClick={() => {
                            window.open(selectedChallenge.fileUrl, "_blank"); // Replace with actual site URL
                          }}
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            margin: "0 auto",
                            textAlign: "center",
                            width: "fit-content",
                          }}
                        >
                          View&nbsp;Site
                        </Button>                        
                      ) : (
                        <Button
                          variant="outlined"
                          startDecorator={<FileDownloadIcon />}
                          onClick={() => {
                            window.open(selectedChallenge.fileUrl, "_blank");
                          }} 
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            margin: "0 auto",
                            textAlign: "center",
                            width: "fit-content",
                          }}                         
                        >
                          Download
                        </Button>
                      )
                    ) : null}
              
              <Divider
                inset="none"
                sx={{
                  borderBottom: "1px dashed #444",
                }}
              />
              {selectedChallenge.hints &&
                selectedChallenge.hints.length > 0 && (
                  <Box>
                    <Typography
                      level="title-md"
                      textAlign="center"
                      sx={{ marginBottom: "16px", fontWeight: "bold" }}
                    >
                      Hints (Note: Each hint costs points to unlock)
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "12px",
                        flexWrap: "wrap",
                      }}
                    >
                      {selectedChallenge.hints.map((hint, index) => {
                        const isUnlocked =
                          unlockedHints[selectedChallenge.id]?.includes(index);
                        const isRevealed = revealedHints.includes(index);

                        return (
                          <Button
                            key={index}
                            variant="outlined"
                            startDecorator={<TipsAndUpdatesIcon />}
                            size="md"
                            color={isUnlocked ? "success" : "primary"}
                            sx={{
                              borderRadius: "8px",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: "rgba(255, 215, 0, 0.2)",
                                borderColor: "gold",
                                color: "gold",
                                transform: "scale(1.05)",
                                boxShadow:
                                  "0px 4px 15px rgba(255, 215, 0, 0.5)",
                              },
                            }}
                            onClick={() => handleRevealHint(index)}
                            disabled={!isUnlocked && totalScore < hint.points}
                          >
                            {`Hint ${index + 1}${
                              isUnlocked
                                ? " (Unlocked)"
                                : ` (${hint.points} pts)`
                            }`}
                          </Button>
                        );
                      })}
                    </Box>
                    {revealedHints.length > 0 && (
                      <Box
                        sx={{
                          textAlign: "center",
                          marginTop: "16px",
                          padding: "12px 16px",
                          borderRadius: "8px",
                          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        {revealedHints.map((hintIndex) => (
                          <Typography
                            key={hintIndex}
                            level="body-md"
                            sx={{ marginBottom: "8px" }}
                          >
                            {selectedChallenge.hints[hintIndex].hint}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              <Stack
                direction="column"
                sx={{
                  padding: "24px",
                  border: "1px dotted #444",
                  borderRadius: "12px",
                }}
              >
                <Typography
                  level="title-md"
                  sx={{
                    marginBottom: "16px",
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  Submit Your Flag
                </Typography>
                <FormControl id="flag" sx={{ width: "100%" }}>
                  <Box
                    sx={{ display: "flex", gap: "10px", alignItems: "center" }}
                  >
                    <Input
                      size="md"
                      startDecorator={<TourIcon />}
                      placeholder="Enter Flag"
                      type="text"
                      sx={{
                        flexGrow: 1,
                        padding: "10px",
                        borderRadius: "8px",
                      }}
                      required
                      onChange={(e) => setFlag(e.target.value)}
                      value={flag}
                      disabled={
                        isCorrect ||
                        submittedChallenges.includes(selectedChallenge.id)
                      }
                      onKeyDown={handleKeyDown}
                    />
                    <Button
                      type="submit"
                      variant="outlined"
                      startDecorator={<FlagIcon />}
                      disabled={
                        isCorrect ||
                        submittedChallenges.includes(selectedChallenge.id) ||
                        (submissionCounts[selectedChallenge.id] || 0) >= 10
                      }
                      sx={{
                        padding: "10px 16px",
                        borderRadius: "8px",
                        transition: "all 0.3s ease",
                      }}
                      onClick={handleSubmitFlag}
                    >
                      {isCorrect ? "Correct!" : "Submit Flag"}
                    </Button>
                  </Box>
                  <div className="flex justify-between">
                    <Typography
                      level="body-md"
                      sx={{
                        marginTop: "10px",
                        textAlign: "center",
                        color: "#888",
                      }}
                      className="text-center"
                    >
                      <EmojiEventsIcon className="mr-2 align-middle" />
                      {solveCounts[selectedChallenge.id] || 0} solved
                    </Typography>
                    <Typography
                      level="body-md"
                      sx={{
                        marginTop: "10px",
                        textAlign: "center",
                        color: "#888",
                      }}
                    >
                      Your Submissions:{" "}
                      {submissionCounts[selectedChallenge.id] || 0}
                      /10
                    </Typography>
                  </div>
                </FormControl>
              </Stack>
            </Box>
            {showConfetti && <Confetti width={800} height={500} />}
          </ModalDialog>
        </Modal>
      )}
    </>
  );
};

export default CTFChallengeTable;
